import { ButtonLink, useDocumentTitle } from "@bodemleven/course-kit/react";
import { Compass, Lock, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useShellCopy } from "../i18n/useShellCopy.ts";
import { paths } from "../paths.ts";

interface MessagePageProps {
  icon: LucideIcon;
  title: string;
  body: string;
  back: { to: string; label: string };
}

const MessagePage = ({ icon: Icon, title, body, back }: MessagePageProps): ReactNode => {
  useDocumentTitle(title);
  return (
    <div className="mx-auto flex max-w-xl flex-col items-center gap-4 px-4 py-20 text-center">
      <span className="flex size-20 items-center justify-center rounded-full bg-primary/10">
        <Icon aria-hidden="true" className="size-10 text-primary" />
      </span>
      <h1 className="text-4xl font-semibold">{title}</h1>
      <p className="text-lg text-muted-foreground">{body}</p>
      <ButtonLink to={back.to} className="mt-2">
        {back.label}
      </ButtonLink>
    </div>
  );
};

export const NotFoundPage = (): ReactNode => {
  const { notFound } = useShellCopy();
  return (
    <MessagePage
      icon={Compass}
      title={notFound.title}
      body={notFound.body}
      back={{ to: paths.home(), label: notFound.back }}
    />
  );
};

/** Signed in, but the page needs a higher role: analyst (lab work) or admin (Lab settings). */
export const NoAccessPage = ({ needs }: { needs: "analyst" | "admin" }): ReactNode => {
  const { noAccess } = useShellCopy();
  return (
    <MessagePage
      icon={Lock}
      title={noAccess[needs].title}
      body={noAccess[needs].body}
      back={{ to: paths.workspace(), label: noAccess.back }}
    />
  );
};
